import type { ReactNode } from 'react';
import { useChartStore } from '@/store/chartStore';
import { Modal } from '@/components/ui/Modal';
import { Z_INDEX } from '@/constants/zIndex';
import { DEFAULT_WILLIAM_ONEIL_SELL_SETTINGS } from '@/indicators/williamOneilSell';
import { NumberField, ToggleSwitch } from '@/components/Chart/ConfigModalFields';
import type {
  WilliamOneilSellSettings,
  Ma50BreakRuleSettings,
  Ma200BreakRuleSettings,
  ClimaxTopRuleSettings,
  ExhaustionGapRuleSettings,
  DistributionDaysRuleSettings,
  PivotStopRuleSettings,
} from '@/types';

interface Props {
  isActive: boolean;
  onClose: () => void;
  onAdd?: () => void;
}

function triggerRedraw() {
  const chart = useChartStore.getState().chart;
  const paneId = useChartStore.getState().indicatorPaneMap.get('WILLIAM_ONEIL_SELL');
  if (chart && paneId) {
    try {
      (chart as unknown as { overrideIndicator: (o: { name: string; styles?: Record<string, unknown> }, paneId?: string) => void })
        .overrideIndicator({ name: 'WILLIAM_ONEIL_SELL', styles: {} }, paneId);
    } catch { /* ignore */ }
  }
}

function RuleSection({ title, hint, enabled, onToggle, children }: {
  title: string;
  hint: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 py-3 border-b border-border last:border-b-0">
      <ToggleSwitch checked={enabled} onChange={onToggle} label={title} hint={hint} />
      <div className={`flex flex-col gap-2.5 pl-1 ${enabled ? '' : 'opacity-40 pointer-events-none'}`}>
        {children}
      </div>
    </div>
  );
}

export function WilliamOneilSellConfigModal({ isActive, onClose, onAdd }: Props) {
  const settings = useChartStore((s) => s.williamOneilSellSettings);
  const setSettings = useChartStore((s) => s.setWilliamOneilSellSettings);
  const setEnabledFlag = useChartStore((s) => s.setWilliamOneilSellEnabled);

  function patchRule<K extends keyof WilliamOneilSellSettings>(
    rule: K,
    patch: Partial<WilliamOneilSellSettings[K]>,
  ) {
    setSettings({ [rule]: { ...settings[rule], ...patch } } as Partial<WilliamOneilSellSettings>);
    triggerRedraw();
  }

  function handleReset() {
    setSettings(DEFAULT_WILLIAM_ONEIL_SELL_SETTINGS);
    triggerRedraw();
  }

  function handleRemove() {
    setEnabledFlag(false);
    const chart = useChartStore.getState().chart;
    const paneMap = useChartStore.getState().indicatorPaneMap;
    if (chart && paneMap.has('WILLIAM_ONEIL_SELL')) {
      try {
        (chart as unknown as { removeIndicator: (paneId: string, name?: string) => void })
          .removeIndicator('candle_pane', 'WILLIAM_ONEIL_SELL');
      } catch { /* ignore */ }
      const next = new Map(paneMap);
      next.delete('WILLIAM_ONEIL_SELL');
      useChartStore.getState().setIndicatorPaneMap(next);
    }
    onClose();
  }

  const ma50 = settings.ma50Break;
  const ma200 = settings.ma200Break;
  const climax = settings.climaxTop;
  const gap = settings.exhaustionGap;
  const dist = settings.distributionDays;
  const pivot = settings.pivotStop;

  return (
    <Modal onClose={onClose} size="xl" zIndex={Z_INDEX.modalElevated} className="max-h-[min(90vh,720px)]">
      <Modal.Header
        onClose={onClose}
        title={
          <div className="flex items-center gap-2.5">
            <span className="text-base font-bold text-modal-text">O'Neil Sell Signals</span>
            <span className="text-sm text-modal-muted">— IBD-style sell rules</span>
            {isActive && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent/15 text-accent font-medium">
                Active
              </span>
            )}
          </div>
        }
      />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left: Settings */}
        <div className="w-[58%] border-r border-border flex flex-col overflow-y-auto">
          <div className="px-5 py-2 flex flex-col">

            <RuleSection
              title="50-day MA Break"
              hint="Close crosses below the moving average on above-average volume"
              enabled={ma50.enabled}
              onToggle={(v) => patchRule('ma50Break', { enabled: v } as Partial<Ma50BreakRuleSettings>)}
            >
              <NumberField label="MA period" value={ma50.maPeriod} min={5} max={200}
                onChange={(v) => patchRule('ma50Break', { maPeriod: v })} suffix="d" />
              <NumberField label="Volume multiplier" value={ma50.volMultiplier} min={1} max={5} step={0.1}
                onChange={(v) => patchRule('ma50Break', { volMultiplier: v })} suffix="x" />
              <NumberField label="Avg volume period" value={ma50.avgVolPeriod} min={5} max={200}
                onChange={(v) => patchRule('ma50Break', { avgVolPeriod: v })} suffix="d" />
            </RuleSection>

            <RuleSection
              title="200-day MA Break"
              hint="Close crosses below the long-term moving average"
              enabled={ma200.enabled}
              onToggle={(v) => patchRule('ma200Break', { enabled: v } as Partial<Ma200BreakRuleSettings>)}
            >
              <NumberField label="MA period" value={ma200.maPeriod} min={20} max={300}
                onChange={(v) => patchRule('ma200Break', { maPeriod: v })} suffix="d" />
            </RuleSection>

            <RuleSection
              title="Climax Top"
              hint="Largest single-bar gain of the advance, on the heaviest volume of the advance"
              enabled={climax.enabled}
              onToggle={(v) => patchRule('climaxTop', { enabled: v } as Partial<ClimaxTopRuleSettings>)}
            >
              <NumberField label="Move lookback" value={climax.lookbackBars} min={20} max={504}
                onChange={(v) => patchRule('climaxTop', { lookbackBars: v })} suffix="d" />
              <NumberField label="Min prior advance" value={Math.round(climax.minAdvancePct * 100)} min={5} max={200}
                onChange={(v) => patchRule('climaxTop', { minAdvancePct: v / 100 })} suffix="%" />
              <NumberField label="Volume multiplier" value={climax.volMultiplier} min={1} max={5} step={0.1}
                onChange={(v) => patchRule('climaxTop', { volMultiplier: v })} suffix="x" />
            </RuleSection>

            <RuleSection
              title="Exhaustion Gap"
              hint="Large gap-up on heavy volume after an extended prior advance"
              enabled={gap.enabled}
              onToggle={(v) => patchRule('exhaustionGap', { enabled: v } as Partial<ExhaustionGapRuleSettings>)}
            >
              <NumberField label="Min gap" value={Math.round(gap.minGapPct * 100)} min={1} max={20}
                onChange={(v) => patchRule('exhaustionGap', { minGapPct: v / 100 })} suffix="%" />
              <NumberField label="Min prior advance" value={Math.round(gap.minAdvancePct * 100)} min={5} max={200}
                onChange={(v) => patchRule('exhaustionGap', { minAdvancePct: v / 100 })} suffix="%" />
              <NumberField label="Advance lookback" value={gap.advanceLookbackBars} min={10} max={252}
                onChange={(v) => patchRule('exhaustionGap', { advanceLookbackBars: v })} suffix="d" />
              <NumberField label="Volume multiplier" value={gap.volMultiplier} min={1} max={5} step={0.1}
                onChange={(v) => patchRule('exhaustionGap', { volMultiplier: v })} suffix="x" />
            </RuleSection>

            <RuleSection
              title="Distribution Days"
              hint="Heavy-volume down days clustering within a trailing window"
              enabled={dist.enabled}
              onToggle={(v) => patchRule('distributionDays', { enabled: v } as Partial<DistributionDaysRuleSettings>)}
            >
              <NumberField label="Window" value={dist.windowBars} min={5} max={60}
                onChange={(v) => patchRule('distributionDays', { windowBars: v })} suffix="d" />
              <NumberField label="Min decline" value={Math.round(dist.minDownPct * 1000) / 10} min={0.1} max={5} step={0.1}
                onChange={(v) => patchRule('distributionDays', { minDownPct: v / 100 })} suffix="%" />
              <NumberField label="Volume multiplier" value={dist.volMultiplier} min={0.5} max={3} step={0.1}
                onChange={(v) => patchRule('distributionDays', { volMultiplier: v })} suffix="x" />
              <NumberField label="Threshold count" value={dist.thresholdCount} min={2} max={15}
                onChange={(v) => patchRule('distributionDays', { thresholdCount: v })} />
            </RuleSection>

            <RuleSection
              title="Pivot Stop"
              hint="7-8%-style stop-loss measured from the most recent confirmed breakout pivot"
              enabled={pivot.enabled}
              onToggle={(v) => patchRule('pivotStop', { enabled: v } as Partial<PivotStopRuleSettings>)}
            >
              <NumberField label="Stop distance" value={Math.round(pivot.stopPct * 1000) / 10} min={3} max={15} step={0.5}
                onChange={(v) => patchRule('pivotStop', { stopPct: v / 100 })} suffix="%" />
              <NumberField label="Pivot lookback" value={pivot.pivotLookbackBars} min={20} max={504}
                onChange={(v) => patchRule('pivotStop', { pivotLookbackBars: v })} suffix="d" />
              <NumberField label="Pivot bars" value={pivot.pivotBars} min={2} max={30}
                onChange={(v) => patchRule('pivotStop', { pivotBars: v })} />
            </RuleSection>

            <button
              type="button"
              onClick={handleReset}
              className="self-start mt-3 text-xs text-modal-muted hover:text-modal-text underline transition-colors"
            >
              Reset all to defaults
            </button>
          </div>
        </div>

        {/* Right: About */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">
          <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">About</p>
          <p className="text-sm text-modal-text leading-relaxed">
            A bundle of six independent William O'Neil / IBD-style sell rules — not one composite
            score. Each can be shown or hidden and re-parameterized on its own; toggle off any
            rule that doesn't fit a particular stock or timeframe.
          </p>
          <p className="text-sm text-modal-text leading-relaxed">
            The Pivot Stop line is not tied to your actual purchase price — O'Neil's own rule is
            to buy at the pivot/breakout, so the 7-8% stop is measured from the most recent
            confirmed pivot the price has broken out above, not a manually entered cost basis.
          </p>

          <div className="h-px bg-border" />

          <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">Legend</p>
          <div className="flex flex-col gap-1.5 text-xs text-modal-muted">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-sm" style={{ background: '#F97316' }} />
              <span>50-day MA break</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-sm" style={{ background: '#DC2626' }} />
              <span>200-day MA break</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-sm" style={{ background: '#EF4444' }} />
              <span>Climax top</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-sm" style={{ background: '#B91C1C' }} />
              <span>Exhaustion gap</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: '#FCA5A5' }} />
              <span>Distribution day</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-sm" style={{ background: '#7C3AED' }} />
              <span>Distribution threshold reached</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-0.5 border-t border-dashed" style={{ borderColor: 'rgba(239,68,68,0.85)' }} />
              <span>Pivot stop-loss line</span>
            </div>
          </div>
        </div>
      </div>

      <Modal.Footer className="!justify-between">
        {isActive ? (
          <button
            type="button"
            onClick={handleRemove}
            className="text-sm px-3 py-2 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors"
          >
            Remove Indicator
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-2 rounded-lg text-modal-muted hover:text-modal-text hover:bg-modal-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={isActive || !onAdd ? onClose : () => { onAdd(); onClose(); }}
            className="text-sm px-4 py-2 rounded-lg bg-accent text-white font-medium hover:bg-accent/90 transition-colors"
          >
            {isActive ? 'Done' : 'Add to Chart'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
